import { requesterOf } from './changeRequester';
import type { TriageColumnId, TriageSort } from './triageViewPrefs';

/** Minimal shape needed to sort triage list rows (avoids pulling the Change model). */
export type TriageSortableRow = {
    slug?: number | string;
    priority?: number;
    updatedAt?: string | Date;
    title?: { [lang: string]: string } | string;
    project?: { name?: string } | null;
    type?: string;
    typeDomain?: string;
    typeSubtype?: string;
    intake?: { sender?: { email?: string } } | null;
    technicalContext?: { rt?: { requestorEmails?: string[] } } | null;
    replyRecipients?: { to?: { emails?: string[] } } | null;
    localizedTitle?: (language: string) => string | null;
};

function typeLabelOf(item: TriageSortableRow): string {
    return String(item.typeSubtype || item.typeDomain || item.type || '').trim();
}

function titleOf(item: TriageSortableRow, language: string): string {
    if (typeof item.localizedTitle === 'function')
        return (item.localizedTitle(language) || '').toLowerCase();
    if (item.title && typeof item.title === 'object')
        return String(item.title[language] || item.title.fr || '').toLowerCase();
    return String(item.title || '').toLowerCase();
}

function sortValue(item: TriageSortableRow, column: TriageColumnId, language: string): string | number {
    switch (column) {
        case 'slug':
            return Number(item.slug) || 0;
        case 'priority':
            return Number(item.priority) || 0;
        case 'title':
            return titleOf(item, language);
        case 'requester':
            return requesterOf(item).toLowerCase();
        case 'project':
            return (item.project?.name || '').toLowerCase();
        case 'type':
            return typeLabelOf(item).toLowerCase();
        case 'updatedAt':
            return item.updatedAt ? new Date(item.updatedAt).getTime() : 0;
        default:
            return 0;
    }
}

/**
 * Sort the full triage queue before pagination.
 * Uses slug desc as tie-breaker so equal timestamps stay stable across page sizes.
 */
export function sortTriageRows<T extends TriageSortableRow>(
    rows: T[],
    sort: TriageSort,
    language: string
): T[] {
    const direction = sort.direction === 'asc' ? 1 : -1;
    return rows.slice().sort((a, b) => {
        const av = sortValue(a, sort.column, language);
        const bv = sortValue(b, sort.column, language);
        let cmp = 0;
        if (typeof av === 'number' && typeof bv === 'number')
            cmp = av - bv;
        else
            cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
        if (cmp !== 0)
            return cmp * direction;
        return (Number(b.slug) || 0) - (Number(a.slug) || 0);
    });
}
